import React, { useMemo, useState } from 'react';
import ReactApexChart from "react-apexcharts";
import { Card, CardHeader, CardTitle, CardContent } from "../../../components/ui/card";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "../../../components/ui/dropdown-menu";
import { Button } from "../../../components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../../../components/ui/tooltip";
import { useIsMobile } from "../../../hooks/use-mobile";
import { cn } from "../../../lib/utils";

// Generate dummy data for the last 15 days
const generateDummyData = () => {
  const data = [];
  const today = new Date();
  
  // Fixed risk score values for consistency
  const riskScores = [0.55, 0.58, 0.62, 0.59, 0.65, 0.68, 0.72, 0.70, 0.66, 0.64, 0.61, 0.59, 0.57, 0.60, 0.63];
  const verifiedValues = [920, 1050, 1180, 1120, 1240, 1320, 1450, 1380, 1260, 1210, 1150, 1100, 1080, 1160, 1220];
  
  for (let i = 14; i >= 0; i--) {
    const date = new Date(today);
    date.setDate(date.getDate() - i);
    
    const riskScore = riskScores[14 - i];
    const verified = verifiedValues[14 - i];
    const unverified = Math.floor(verified * riskScore / (1 - riskScore));
    
    data.push({
      date: date.toISOString().split('T')[0],
      risk_score: riskScore,
      verified: verified,
      unverified: unverified
    });
  }
  
  return data;
};

const dummyDailyData = generateDummyData();

const DailyFindings = () => {
  const isMobile = useIsMobile();
  const [selectedDays, setSelectedDays] = useState(5);
  
  const riskScorePerDay = useMemo(() => {
    // Apply days limit to dummy data
    return dummyDailyData.slice(-selectedDays);
  }, [selectedDays]);

  const chartOptions = useMemo(() => ({
    chart: {
      type: "area",
      toolbar: { show: false },
      zoom: { enabled: false },
      background: "transparent",
      fontFamily: "Inter, sans-serif",
      animations: {
        enabled: true,
        easing: 'easeinout',
        speed: 800,
        animateGradually: {
          enabled: true,
          delay: 150
        },
        dynamicAnimation: {
          enabled: true,
          speed: 350
        }
      }
    },
    xaxis: {
      categories: riskScorePerDay.map((d) => d.date),
      title: {
        text: "Date",
        style: {
          color: "#000000",
          fontSize: isMobile ? "12px" : "14px",
          fontWeight: "600",
        },
      },
      labels: {
        style: {
          colors: "#000000",
          fontSize: isMobile ? "10px" : "12px",
        },
        formatter: (value) => {
          // Format date to show only month-day
          const date = new Date(value);
          return `${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getDate().toString().padStart(2, '0')}`;
        }
      },
      axisBorder: { 
        color: "#C0ACF3",
        show: true 
      },
      axisTicks: { 
        color: "#C0ACF3",
        show: true 
      },
    },
    yaxis: {
      title: {
        text: "Risk Score (%)",
        style: {
          color: "#000000",
          fontSize: isMobile ? "12px" : "14px",
          fontWeight: "600",
        },
      },
      labels: {
        style: {
          colors: "#000000",
          fontSize: isMobile ? "10px" : "12px",
        },
        formatter: (val) => `${val}%`
      },
      min: 0,
      max: 100,
    },
    grid: {
      borderColor: "#C0ACF3",
      strokeDashArray: 4,
      padding: {
        top: isMobile ? 10 : 15,
        right: isMobile ? 10 : 15,
        bottom: isMobile ? 5 : 8,
        left: isMobile ? 10 : 15,
      },
      show: true,
      xaxis: {
        lines: {
          show: true
        }
      },
      yaxis: {
        lines: {
          show: true
        }
      }
    },
    stroke: {
      curve: "smooth",
      width: isMobile ? 2 : 3,
      colors: ["#C0ACF3"],
    },
    markers: {
      size: isMobile ? 4 : 6,
      colors: ["#7B2BBE"],
      strokeColors: "#7B2BBE",
      strokeWidth: isMobile ? 1 : 2,
      hover: { 
        sizeOffset: 3 
      },
      discrete: riskScorePerDay.map((d, index) => ({
        seriesIndex: 0,
        dataPointIndex: index,
        fillColor: d.risk_score > 0 ? "#7B2BBE" : "#E5E7EB",
        strokeColor: d.risk_score > 0 ? "#7B2BBE" : "#9CA3AF",
        size: d.risk_score > 0 ? (isMobile ? 3 : 5) : (isMobile ? 2 : 3),
      }))
    },
    fill: {
      type: "solid",
      colors: ["#7B2BBE17"]
    },
    tooltip: {
      theme: "light",
      custom: function({ series, seriesIndex, dataPointIndex }) {
        const data = riskScorePerDay[dataPointIndex];
        const date = new Date(data.date).toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric'
        });
        
        return `
          <div style="padding: 10px; background: white; border-radius: 6px; box-shadow: 0 4px 6px rgba(0,0,0,0.1);">
            <div style="font-weight: 600; margin-bottom: 4px;">${date}</div>
            <div>Risk Score: <span style="color: #441A9A; font-weight: 600;">${series[seriesIndex][dataPointIndex]}%</span></div>
            <div>Active: <span style="color: #059669;">${data.verified}</span></div>
            <div>Unvalidated: <span style="color: #DC2626;">${data.unverified}</span></div>
          </div>
        `;
      }
    },
    legend: {
      show: false
    },
    dataLabels: {
      enabled: false
    }
  }), [isMobile, riskScorePerDay]);

  const series = useMemo(() => [{
    name: "Risk Score",
    data: riskScorePerDay.map((d) => {
      const riskPercentage = Math.round(d.risk_score * 100);
      return riskPercentage;
    }),
  }], [riskScorePerDay]);

  if (!riskScorePerDay || riskScorePerDay.length === 0) {
    return (
      <Card className={cn(
        "border-[#CBC8C89C] h-full flex flex-col justify-center items-center",
        "bg-white",
        isMobile ? "rounded-xl p-4" : "rounded-xl p-5"
      )}>
        <div className="text-center text-gray-600">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="mx-auto mb-3">
            <path d="M9 12l2 2 4-4"></path>
            <circle cx="12" cy="12" r="10"></circle>
          </svg>
          <p className="text-sm">No risk data available</p>
          <p className="text-xs mt-1">Risk trend data will appear here once available</p>
        </div>
      </Card>
    );
  }

  return (
    <TooltipProvider>
      <Card className={cn(
        "border-[#CBC8C89C] h-full flex flex-col relative",
        "bg-white",
        isMobile ? "rounded-xl p-4" : "rounded-xl p-3"
      )}>
      <CardHeader className={cn(
        "flex flex-row items-start justify-between space-y-0 p-0",
        isMobile ? "pb-3" : "pb-4"
      )}>
        <div className="flex flex-col">
          <CardTitle className={cn(
            "font-semibold text-black font-inter tracking-tight",
            isMobile ? "text-sm" : "text-lg"
          )}>
            Daily Risk Trends
          </CardTitle>
        </div>
        <div className="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button 
                variant="outline" 
                size="sm"
                className="h-8 text-xs bg-[#44087D]/10 border-[#C0ACF3] hover:bg-[#C0ACF3]/10"
              >
                {selectedDays} days
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-24 bg-white border border-gray-200 shadow-lg">
              {[5, 10, 15].map((days) => (
                <DropdownMenuItem 
                  key={days}
                  onClick={() => setSelectedDays(days)}
                  className={cn(
                    "cursor-pointer text-center justify-center bg-white hover:bg-gray-50",
                    selectedDays === days && "bg-[#C0ACF3]/20 text-[#7B2BBE] font-medium hover:bg-[#C0ACF3]/30"
                  )}
                >
                  {days} days
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </CardHeader>
      
      <CardContent className="p-0 relative h-[180px] md:h-[220px]">
        <ReactApexChart
          options={chartOptions}
          series={series}
          type="area"
          height={isMobile ? 180 : 220}
          width="100%"
        />
      </CardContent>
      <Tooltip>
        <TooltipTrigger asChild>
          <button className={`absolute bottom-3 right-3 w-5 h-5 rounded-full border border-black/35 bg-white flex items-center justify-center text-black/35 hover:bg-gray-50 transition-colors ${isMobile ? 'text-xs' : 'text-sm'}`}>
            <span className="font-semibold">i</span>
          </button>
        </TooltipTrigger>
        <TooltipContent>
          <p>Daily risk trends information will be displayed here</p>
        </TooltipContent>
      </Tooltip>
    </Card>
    </TooltipProvider>
  );
};

export default DailyFindings;